import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiSend } from "../lib/api.js";
import { drainCursorPages, shouldThrowDrainBudgetError } from "../lib/queryPageDrain.js";
import { usePageVisibility } from "../lib/usePageVisibility.js";
import type {
  CreateDeviceInput,
  Device,
  DeviceDiagnostics,
  ListDevicesResponse,
  ListDeviceSessionsResponse,
} from "./types.js";

/** Hooks for the devices admin surface. Soft-delete only. */

const DEVICES_KEY = ["devices"] as const;

export function useDevices() {
  return useQuery<ListDevicesResponse, Error>({
    queryKey: [...DEVICES_KEY, "list"],
    queryFn: async ({ signal }) => ({
      items: await drainCursorPages((cursor) =>
        apiGet<ListDevicesResponse>(
          "/v1/devices",
          { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
          signal,
        ),
      ),
      nextCursor: null,
    }),
    throwOnError: shouldThrowDrainBudgetError,
    staleTime: 60_000,
    // GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — the devices list is a
    // heavy read (now also carries the embedded connectivity join for
    // every row). Refetching the whole roster on every window focus
    // added avoidable bursts to the per-IP budget; rely on the 60s
    // staleTime + explicit invalidation on mutations instead.
    refetchOnWindowFocus: false,
  });
}

/**
 * Sprint 22 Wave B (S22-B1 follow-up) — narrow list of unassigned
 * active devices, used by the Vehicle Detail "Assign Device" picker.
 *
 * Hits the additive server filter `?unassigned=true&status=active`
 * (devices.dto.ts) so the picker is bounded to the eligible set
 * regardless of total fleet size — the previous client-side filter
 * over the first `limit:200` page silently dropped eligible devices
 * for tenants with more than 200 devices. The limit here is still
 * generous (500) but operates over the eligible subset only.
 */
export function useUnassignedActiveDevices(opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery<ListDevicesResponse, Error>({
    queryKey: [...DEVICES_KEY, "list", "unassigned-active"],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryFn: ({ signal, pageParam }) =>
      apiGet<ListDevicesResponse>(
        "/v1/devices",
        // `apiGet` serialises params via URLSearchParams; the server
        // coerces "true" via `z.coerce.boolean()` (devices.dto.ts).
        {
          limit: 500,
          status: "active",
          unassigned: "true",
          ...(pageParam === undefined ? {} : { cursor: String(pageParam) }),
        },
        signal,
      ),
    staleTime: 30_000,
    // FIX-02 — heavy `/v1/devices` read; no focus-refetch storm.
    refetchOnWindowFocus: false,
    enabled: opts.enabled !== false,
  });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation<Device, Error, CreateDeviceInput>({
    mutationFn: (input) => apiSend<Device>("POST", "/v1/devices", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEVICES_KEY }),
  });
}

export function useDisableDevice(id: string) {
  const qc = useQueryClient();
  return useMutation<Device, Error, void>({
    mutationFn: () => apiSend<Device>("DELETE", `/v1/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEVICES_KEY }),
  });
}

/**
 * Sprint 21 Wave B (Phase 1C) — partial-update a device.
 *
 * Backed by `PATCH /v1/devices/:id` (Sprint 11), which accepts
 * `{ vehicleId?: string | null, status?: 'active' | 'disabled' }`.
 * Used by the device edit drawer to reassign a device to a
 * different vehicle without going through delete + re-register
 * (which would corrupt session/diagnostics history).
 *
 * IMEI is intentionally NOT in the patch shape — re-keying a
 * registered IMEI corrupts the gateway allow-list join. Operators
 * who need to change the IMEI must delete + re-register through
 * the create flow (DL-036).
 */
export function useUpdateDevice(id: string) {
  const qc = useQueryClient();
  return useMutation<Device, Error, { vehicleId?: string | null; status?: "active" | "disabled" }>({
    mutationFn: (input) => apiSend<Device>("PATCH", `/v1/devices/${id}`, input),
    /*
      GT-FLEET-OWNER-SMOKE-STRUCTURAL-RECOVERY-01 — this used to
      invalidate the device list alone, which left the OTHER side of the
      binding stale. A vehicle assignment changes the vehicle's
      `activeDeviceCount` (the Vehicles list "No tracker" projection and
      the vehicle detail read model) and opens or closes a row in the
      vehicle's device history. The unassign hook below already
      invalidated the history; the assign path did not, so the two read
      models could disagree until a manual refresh.

      `invalidateDeviceBinding` is the one place that knows the full
      consumer set, so assign / reassign / restore cannot drift apart.
    */
    onSuccess: () => invalidateDeviceBinding(qc),
  });
}

/**
 * Every cache a device↔vehicle binding change is visible in.
 *
 * Prefix keys are used deliberately: `["fleet","vehicles"]` covers the
 * list, the live list and every per-id detail entry, so a new vehicle
 * read cannot silently escape invalidation.
 */
function invalidateDeviceBinding(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: DEVICES_KEY });
  void qc.invalidateQueries({ queryKey: ["fleet", "vehicles"] });
  void qc.invalidateQueries({ queryKey: ["vehicles", "device-history"] });
}

/**
 * Sprint 20.1 PR E — restore a previously archived (status='disabled')
 * device to status='active'. Goes through the existing PATCH route
 * which accepts `{ status: 'active' | 'disabled' }`. No migration
 * needed — the soft-delete model already supports re-enabling.
 */
export function useReactivateDevice(id: string) {
  const qc = useQueryClient();
  return useMutation<Device, Error, void>({
    mutationFn: () => apiSend<Device>("PATCH", `/v1/devices/${id}`, { status: "active" }),
    // A restored device re-occupies its vehicle slot, so the vehicle
    // read models move too.
    onSuccess: () => invalidateDeviceBinding(qc),
  });
}

/**
 * GT-DEVICE-ASSIGNMENT-CONSISTENCY-UNASSIGN-04 — canonical detach.
 *
 * `POST /v1/devices/:id/unassign` ends any open assignment row AND
 * clears `devices.vehicle_id` idempotently. Both the device edit
 * "Leave unassigned" and the Vehicle detail "Unassign device" action
 * call this same endpoint (id passed at mutate time so one hook instance
 * serves a list of vehicle-history rows). Invalidates the device list
 * and the vehicle device-history so both surfaces refresh consistently.
 */
export interface UnassignDeviceResult {
  readonly device: Device;
  readonly previousVehicleId: string | null;
  readonly endedCount: number;
}

export function useUnassignDevice() {
  const qc = useQueryClient();
  return useMutation<UnassignDeviceResult, Error, string>({
    mutationFn: (id) => apiSend<UnassignDeviceResult>("POST", `/v1/devices/${id}/unassign`),
    onSuccess: () => invalidateDeviceBinding(qc),
  });
}

export function useDeviceSessions(deviceId: string) {
  return useQuery<ListDeviceSessionsResponse, Error>({
    queryKey: [...DEVICES_KEY, "sessions", deviceId],
    queryFn: ({ signal }) =>
      apiGet<ListDeviceSessionsResponse>(`/v1/devices/${deviceId}/sessions`, { limit: 50 }, signal),
    enabled: Boolean(deviceId),
    staleTime: 30_000,
  });
}

/**
 * Sprint 11 Wave C — per-device diagnostics hook.
 * `online` = `lastPacketAt` within the last 15 min.
 */
export function useDeviceDiagnostics(deviceId: string) {
  // GT-…-16 (E) — gate the 30s poll on page visibility. It was
  // unconditional, so a backgrounded tab kept hitting
  // `/v1/devices/:id/diagnostics` every 30s, adding needless load that
  // (combined with the other pollers) can approach the API rate limit and
  // surface as intermittent "couldn't load" errors. Other pollers already
  // use this guard; align this one.
  const visible = usePageVisibility();
  return useQuery<DeviceDiagnostics, Error>({
    queryKey: [...DEVICES_KEY, "diagnostics", deviceId],
    queryFn: ({ signal }) =>
      apiGet<DeviceDiagnostics>(`/v1/devices/${deviceId}/diagnostics`, undefined, signal),
    enabled: Boolean(deviceId),
    staleTime: 15_000,
    refetchInterval: visible ? 30_000 : false,
  });
}

/**
 * Sprint 23 Wave C (S23-C3) — tenant device-quota usage projection.
 *
 * `max=null` means unlimited; the chip renders that as "Unlimited".
 * The query is invalidated together with the rest of the devices
 * cache on create/update/delete so the chip reflects the live
 * count immediately after a successful registration or archive.
 */
export interface DeviceQuotaUsage {
  readonly used: number;
  readonly max: number | null;
}

export function useDeviceQuota() {
  return useQuery<DeviceQuotaUsage, Error>({
    queryKey: [...DEVICES_KEY, "quota"],
    queryFn: ({ signal }) => apiGet<DeviceQuotaUsage>("/v1/devices/quota", undefined, signal),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Sprint 49.0 (Batch A — theme 5 closure). DTO mirror of the backend
 * `DeviceAssignmentDto` shape. Both endpoints
 * (`/v1/vehicles/:id/devices/history` and
 * `/v1/devices/:id/vehicles/history`) return `{ items: […] }` with this
 * row shape.
 */
export interface DeviceAssignmentDto {
  readonly id: string;
  readonly tenantId: string;
  readonly vehicleId: string;
  // `deviceId` is NULL once the device has been moved away (sprint -06)
  // or permanently deleted (sprint -07): the FK fires ON DELETE SET NULL,
  // detaching history while preserving the row. The snapshots below keep
  // the physical tracker identifier visible after that.
  readonly deviceId: string | null;
  readonly deviceImeiSnapshot: string | null;
  readonly deviceTypeSnapshot: string | null;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly assignedBySub: string | null;
}

interface ListDeviceAssignmentsResponse {
  readonly items: ReadonlyArray<DeviceAssignmentDto>;
}

/**
 * Sprint 49.0 — devices ever bound to this vehicle (newest first).
 * Powers the new `VehicleDeviceHistorySection` mounted on
 * `/vehicles/:id`. Symmetric to the Sprint 48.3 driver-history hook.
 */
export function useVehicleDeviceHistory(vehicleId: string | undefined) {
  return useQuery<ListDeviceAssignmentsResponse, Error>({
    queryKey: ["vehicles", "device-history", vehicleId],
    queryFn: ({ signal }) =>
      apiGet<ListDeviceAssignmentsResponse>(
        `/v1/vehicles/${vehicleId}/devices/history`,
        undefined,
        signal,
      ),
    enabled: Boolean(vehicleId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Sprint 49.0 — vehicles this device was ever bound to (newest first).
 * Mirror of `useVehicleDeviceHistory` from the device side; reads the
 * same `vehicle_device_assignments` table filtered by `deviceId`.
 */
export function useDeviceVehicleHistory(deviceId: string | undefined) {
  return useQuery<ListDeviceAssignmentsResponse, Error>({
    queryKey: ["devices", "vehicle-history", deviceId],
    queryFn: ({ signal }) =>
      apiGet<ListDeviceAssignmentsResponse>(
        `/v1/devices/${deviceId}/vehicles/history`,
        undefined,
        signal,
      ),
    enabled: Boolean(deviceId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
